/**
 * Helpers for the Shift Window and Break times of Capacity Planning. The API
 * carries them as plain "HH:mm:ss" strings (see CapacityPlanTime on the API
 * side - HotChocolate 13.9.8 ships no TimeOnly scalar, so a String is used
 * both ways); the UI edits them as "HH:mm" clock times.
 */

const clockPattern = /^(\d{1,3}):([0-5]\d)(?::([0-5]\d(?:\.\d+)?))?$/;

const secondsPerHour = 3600;
const secondsPerMinute = 60;

const pad = (value: number) => `${value}`.padStart(2, '0');

/** Accepts a "HH:mm[:ss]" string. Returns null when unparsable. */
export function timeSpanToSeconds(
  value: string | null | undefined,
): number | null {
  if (!value) {
    return null;
  }

  const clock = clockPattern.exec(value);
  if (!clock) {
    return null;
  }

  const [, hours, minutes, seconds] = clock;
  return (
    Number(hours) * secondsPerHour +
    Number(minutes) * secondsPerMinute +
    Math.round(Number(seconds ?? 0))
  );
}

/** 30600 -> "08:30:00". */
export function secondsToTimeSpan(totalSeconds: number): string {
  const safe = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(safe / secondsPerHour);
  const minutes = Math.floor((safe % secondsPerHour) / secondsPerMinute);
  const seconds = safe % secondsPerMinute;

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/** "08:30:00" -> "08:30". Returns an empty string when the value is missing. */
export function timeSpanToHhMm(value: string | null | undefined): string {
  const totalSeconds = timeSpanToSeconds(value);
  if (totalSeconds === null) {
    return '';
  }

  const hours = Math.floor(totalSeconds / secondsPerHour);
  const minutes = Math.floor(
    (totalSeconds % secondsPerHour) / secondsPerMinute,
  );

  return `${pad(hours)}:${pad(minutes)}`;
}

/** "08:30" -> "08:30:00", the shape the API's RunSettingsInput expects. Returns null when the input is not a clock time. */
export function hhMmToTimeSpan(value: string): string | null {
  const totalSeconds = timeSpanToSeconds(value);
  return totalSeconds === null ? null : secondsToTimeSpan(totalSeconds);
}

/** 5025 -> "1h 23m". Used for route durations in the Result tab. */
export function formatSecondsAsHoursMinutes(totalSeconds: number): string {
  const safe = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(safe / secondsPerHour);
  const minutes = Math.floor((safe % secondsPerHour) / secondsPerMinute);

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

/**
 * 29500 -> "08:11". Formats a step's `arrivalSeconds` (seconds from midnight)
 * as a "HH:mm" clock time, for the Result tab's stops table.
 */
export function secondsFromMidnightToHhMm(seconds: number): string {
  const safe = Math.max(0, Math.round(seconds));
  const hours = Math.floor(safe / secondsPerHour) % 24;
  const minutes = Math.floor((safe % secondsPerHour) / secondsPerMinute);

  return `${pad(hours)}:${pad(minutes)}`;
}
